/**
 * ViewTabs Component
 *
 * Modern underline-style tabs for switching between different views:
 * - Board (Kanban)
 * - Table (List)
 * - Calendar
 *
 * Features:
 * - Underline indicator with smooth animation
 * - Clear active/hover states
 * - Keyboard navigation support
 * - Accessible with ARIA labels
 */

import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import '../../styles/index.scss';

const VIEW_CONFIGS = {
  board: {
    key: 'board',
    label: 'Board',
    icon: <AppstoreOutlined />,
    ariaLabel: 'Kanban board view',
  },
  table: {
    key: 'table',
    label: 'Table',
    icon: <UnorderedListOutlined />,
    ariaLabel: 'Table list view',
  },
  calendar: {
    key: 'calendar',
    label: 'Calendar',
    icon: <CalendarOutlined />,
    ariaLabel: 'Calendar view',
  },
  timeline: {
    key: 'timeline',
    label: 'Timeline',
    icon: <FieldTimeOutlined />,
    ariaLabel: 'Timeline view',
  },
};

const ViewTabs = ({
  activeView = 'board',
  onViewChange,
  availableViews = ['board', 'table', 'calendar'],
  showIcons = true,
  compact = false,
  className = '',
}) => {
  const tabsRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  // Update indicator position when active view changes
  useEffect(() => {
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector(`[data-view="${activeView}"]`);
      if (activeTab) {
        const tabsRect = tabsRef.current.getBoundingClientRect();
        const activeRect = activeTab.getBoundingClientRect();
        setIndicatorStyle({
          left: activeRect.left - tabsRect.left,
          width: activeRect.width,
        });
      }
    }
  }, [activeView]);

  const handleTabClick = (viewKey) => {
    if (onViewChange && viewKey !== activeView) {
      onViewChange(viewKey);
    }
  };

  const handleKeyDown = (e, viewKey, index) => {
    const views = availableViews;
    let newIndex = index;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = index > 0 ? index - 1 : views.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = index < views.length - 1 ? index + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = views.length - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleTabClick(viewKey);
        return;
      default:
        return;
    }

    // Focus the new tab
    const newViewKey = views[newIndex];
    const newTab = tabsRef.current?.querySelector(`[data-view="${newViewKey}"]`);
    if (newTab) {
      newTab.focus();
    }
  };

  const tabs = availableViews
    .filter((viewKey) => VIEW_CONFIGS[viewKey])
    .map((viewKey) => VIEW_CONFIGS[viewKey]);

  return (
    <div
      className={`task-view-tabs ${compact ? 'task-view-tabs--compact' : ''} ${className}`}
      role="tablist"
      aria-label="View options"
      ref={tabsRef}
    >
      {tabs.map((tab, index) => {
        const isActive = activeView === tab.key;

        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            data-view={tab.key}
            className={`task-view-tabs__tab ${isActive ? 'task-view-tabs__tab--active' : ''}`}
            onClick={() => handleTabClick(tab.key)}
            onKeyDown={(e) => handleKeyDown(e, tab.key, index)}
            aria-selected={isActive}
            aria-label={tab.ariaLabel}
            tabIndex={isActive ? 0 : -1}
          >
            {showIcons && (
              <span className="task-view-tabs__icon" aria-hidden="true">
                {tab.icon}
              </span>
            )}
            <span className="task-view-tabs__label">{tab.label}</span>
          </button>
        );
      })}

      {/* Animated underline indicator */}
      <div
        className="task-view-tabs__indicator"
        style={{
          transform: `translateX(${indicatorStyle.left}px)`,
          width: `${indicatorStyle.width}px`,
        }}
        aria-hidden="true"
      />
    </div>
  );
};

ViewTabs.propTypes = {
  activeView: PropTypes.oneOf(['board', 'table', 'calendar', 'timeline']),
  onViewChange: PropTypes.func,
  availableViews: PropTypes.arrayOf(
    PropTypes.oneOf(['board', 'table', 'calendar', 'timeline'])
  ),
  showIcons: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default ViewTabs;
